import { LoadingButton } from "@mui/lab";
import {
  Grid,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { ConfirmDangerModalComponent, ErrorModal } from "@soqs/common";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { uploadFiles } from "../../../features/API";
import UploadWidget from "./UploadWidget";
import { PAGES } from "../CreateBookingWrapper";
import { ErrorResponse } from "../../../models/ErrorResponse";
import {
  bookingTaxpayerID,
  bookings,
  cancelBooking,
  cancelBookingAsync,
  createBookingFormState,
  getAllAvailableDates,
  requiredFiles,
  selectedDate,
  setBookingCreatedSuccessfully,
  setDates,
  setDisplayBookings,
  setDocumentsStillRequired,
  setPage,
  setSelectedTime,
  setTimes,
  updateCreateBookingFormState,
  uploadingDocsForCreatedBooking,
} from "../../../features/BookingsSlice";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  getErrorModalInnerHTML,
  getErrorModalTitle,
} from "../../../features/Helper";
import { Docs } from "../../../models/RequiredDocs";
import { BOOKING_CANCELLED_GOING_BACK } from "../../../features/Constants";

const UploadDocs = () => {
  const dispatch = useDispatch();
  const requiredDocs = useSelector(requiredFiles);
  const bookingForm = useSelector(createBookingFormState);
  const createdBookings = useSelector(bookings);
  const selectedBookingDate = useSelector(selectedDate);
  const taxPayer = useSelector(bookingTaxpayerID);
  const createBookingUploadingDocs = useSelector(uploadingDocsForCreatedBooking);

  const [docsToBeUploaded, setDocsToBeUploaded] = useState<Docs[]>([]);

  //prettier-ignore
  const [files, setFiles] = useState<Array<Array<File>>>([]);
  const [uploading, setUploading] = useState(false);
  const [confirmUploadLaterModal, setConfirmUploadLaterModal] = useState(false);
  const [confirmBackModal, setConfirmBackModal] = useState(false);
  const [confirmCreatedBackModal, setCreatedConfirmBackModal] = useState(false);

  //prettier-ignore
  const [errors, setErrors] = useState<ErrorResponse>({});
  const [disabledUpload, setDisabledUpload] = useState(true);

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    setDocsToBeUploaded(requiredDocs);
  }, [requiredDocs, setDocsToBeUploaded]);

  const upload = (fg: Array<Array<File>>) => {
    let files_to_upload = [];
    for (let fileGroup in fg) {
      for (let file of fg[fileGroup]) {
        let nf = new File([file], fileGroup + "_" + file.name, {
          type: file.type,
        });
        files_to_upload.push(nf);
      }
    }
    setUploading(true);
    uploadFiles(files_to_upload)
      .then((data: ErrorResponse) => {
        setUploading(false);
        if (data.error) {
          setErrors(data);
        } else {
          dispatch(setDocumentsStillRequired(false));
          dispatch(setPage(PAGES.Confirmation));
        }
      })
      .catch((err) => {
        setUploading(false);
        setErrors(err);
      });
  };

  const setFilesAndCheck = (files_l: Array<Array<File>>) => {
    let disabled = false;
    if (Object.keys(files_l).length !== docsToBeUploaded.length) {
      disabled = true;
    } else {
      for (let filegroup in files_l) {
        if (files_l[filegroup].length === 0) {
          disabled = true;
          break;
        }
      }
    }
    
    setFiles(files_l);
    setDisabledUpload(disabled);
  };

  const getAvailableDates = () => {
    const data = {
      date: selectedBookingDate ? new Date(selectedBookingDate).toISOString() : new Date().toISOString(),
      bookingTaxpayerID: taxPayer,
      branchCode: bookingForm?.values?.branch,
      reasonPermutationId: bookingForm?.values?.reason
    };
    dispatch(setDates([]));
    dispatch(setTimes({ morning: [], afternoon: [] }));
    dispatch(setSelectedTime(''));
    dispatch(getAllAvailableDates(data));
  }

  const back = (): void => {
    if(createBookingUploadingDocs) {
      if (files?.length) {
        setCreatedConfirmBackModal(true)
      } else {
        dispatch(updateCreateBookingFormState(null));
        dispatch(setDisplayBookings(true));
        dispatch(setDates([]));
      }
    } else {
      setConfirmBackModal(true);
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "60%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {docsToBeUploaded?.map((doc: Docs, index: number) => (
          <UploadWidget
            sectionName={doc.Description}
            cbSetFiles={(f: Array<File>) => {
              let newf = files;
              newf[index] = f;
              setFilesAndCheck(newf);
            }}
            setErrors={setErrors}
            validation={true}
            key={doc.Type}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#E3EFF57D",
          borderRadius: "8px",
          justifyContent: "space-between",
          mt: "12px",
          mb: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: "8px" }}>
          <InfoOutlinedIcon color="primary" sx={{ mr: "16px" }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography color="primary">
              <strong>Please Note:</strong>
            </Typography>
            <Typography color="primary">
              You can upload your documents at a later stage, but you will only
              have 24 hours to upload the required documents.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: "8px", mr: "8px" }}>
          <Typography
            color="primary"
            onClick={() => {
              setConfirmUploadLaterModal(true);
            }}
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            <strong>Upload Later</strong>
          </Typography>
        </Box>
      </Box>

      <Grid item xs={12} sx={{ display: "flex", alignItems: "flex-end", pb: "12px" }}>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="space-between"
        >
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={back}
            >
              Back
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={uploading}
              onClick={() => {
                upload(files);
              }}
              disabled={disabledUpload ?? false}
            >
              Complete Booking
            </LoadingButton>
          </Grid>
        </Grid>

        <ConfirmDangerModalComponent
          open={confirmUploadLaterModal}
          onClose={() => {
            setConfirmUploadLaterModal(false);
          }}
          cbConfirm={() => {
            dispatch(setDocumentsStillRequired(true));
            setConfirmUploadLaterModal(false);
            dispatch(setPage(PAGES.Confirmation));
          }}
          title="Upload Documents Later"
          subheader="You will have 24 hours to upload your documents. If not, you will lose your booking slot."
          confirmText="Upload Later"
          cancelText="Cancel"
        />

        <ConfirmDangerModalComponent
          open={confirmBackModal}
          onClose={() => {
            setConfirmBackModal(false);
          }}
          cbConfirm={async () => {
            setConfirmBackModal(false);
            const result = await dispatch(cancelBookingAsync({cancelReason: BOOKING_CANCELLED_GOING_BACK, bookingId: createdBookings[0].BookingID}));
            if((result as any).meta.requestStatus === "fulfilled") {
              dispatch(setBookingCreatedSuccessfully(false));
              getAvailableDates();
              dispatch(setPage(PAGES.CreateBooking));
            }
          }}
          title="Are you sure?"
          subheader="Your selected date and time may not be available if you go back. You will have to select a new date and time."
          confirmText="Continue"
          cancelText="Cancel"
        />

        <ConfirmDangerModalComponent
          open={confirmCreatedBackModal}
          onClose={() => {
            setCreatedConfirmBackModal(false);
          }}
          cbConfirm={async () => {
            setCreatedConfirmBackModal(false);
            dispatch(updateCreateBookingFormState(null));
            dispatch(setDisplayBookings(true));
            dispatch(setDates([]));
          }}
          title="Are you sure?"
          subheader="Your uploaded documents will not be saved if you go back. You will need to upload the required documents again."
          confirmText="Continue"
          cancelText="Cancel"
        />

        <ErrorModal
          title={getErrorModalTitle(errors)}
          open={Object.keys(errors).length !== 0}
          onClose={() => {
            if (errors?.fatal) {
              dispatch(cancelBooking(null));
            }
            setErrors({});
          }}
          subheader={
            Array.isArray(errors?.error) ? (
              <ul>
                {errors?.error.map((e) => (
                  <li key={e}>{e.replace("- ", "")}</li>
                ))}
              </ul>
            ) : (
              <p
                dangerouslySetInnerHTML={{
                  __html: getErrorModalInnerHTML(errors),
                }}
              ></p>
            )
          }
        />
      </Grid>
    </Box>
  );
};

export default UploadDocs;
